import {
  InstitutionalMember,
  KycStatuses,
  P2PAccess,
} from "@services/models/Common/GetProfile";
import { GetTokenListData } from "@services/models/Common/GetTokenList";
import { GetTradesForTakerData } from "@services/models/Prime/GetTradesForTakerModel";
import secureLocalStorage from "react-secure-storage";
import {
  ADD_HISTORY,
  ADD_MERCHANT,
  CLEAR_TOKEN_LIST,
  RESET_STATE,
  SET_ERROR,
  SET_HAS_MERCHANT,
  SET_IS_DARK,
  SET_IS_PERSONAL,
  SET_PROFILE_LOADED,
  SET_PROFILE_MERCHANT_DATA,
  SET_RAMP_DETAILS_BUY,
  SET_RAMP_DETAILS_SELL,
  SET_RELOAD_TRADES,
  SET_SELECTED_TRADE_ID,
  SET_TOKEN_LIST,
  SET_TOKEN_LIST_LOADING,
  SET_USER,
  SET_WEBSOCKET_LOADED,
  UPDATE_TOKEN_BALANCE,
} from "./types";

export interface MerchantDetail {
  userId: number;
  merchantId: number;
  merchantURL: string | null;
  merchantName: string;
  merchantUUID: string;
  active: boolean;
  storeName: string;
  profileImage: string;
  businessMode: string;
  country: string;
  createdAt: string;
  makerP2pRampFiat: number | null;
  makerP2pRampCrypto: number | null;
  allowKYCBypass: number | null;
  categoryName: string | null;
  categoryType: string | null;
  currencyImage: string | null;
  accountCategoryId: number | null;
}

export interface MerchantBalance {
  merchantId: number;
  balanceInDollars: number;
}

export interface ProfileMerchantData {
  userId: number;
  kycStatuses: KycStatuses;
  username: string;
  emailId: string;
}

export interface AppState {
  isPersonal: boolean;
  isDark: boolean;
  user: {
    userId: number | null;
    emailId: string;
    status: string;
    whatsappNumber: string;
    notificationSound: number;
    countryCode: string;
    userUUID: string;
    profileImage: string;
    firstName: string;
    currentMerchantId: number | null;
    merchantDetails: MerchantDetail[];
    personalBalance: number;
    merchantBalances: MerchantBalance[];
    p2pAccess: P2PAccess;
    kycStatuses: KycStatuses;
    createdAt: string;
    intercomUserHash: string;
    refBy: string;
    accountType:
      | "individual"
      | "institutional"
      | "affiliate"
      | "cashier"
      | "settlement";
    affiliateStatus: "inactive" | "requested" | "approved" | "rejected";
    institutionalMember: InstitutionalMember | null;
  };
  profileMerchantData: ProfileMerchantData | null;
  hasMerchant: boolean;
  websocketLoaded: boolean;
  error: boolean;
  history: string[];
  addMerchant: boolean;

  // ✅ New PrimeTaker state
  selectedTradeId: number | null;
  rampDetailsBuy: GetTradesForTakerData | null;
  rampDetailsSell: GetTradesForTakerData | null;
  reloadTrades: number;
  isCreateMerchant: boolean;

  // Token list caching
  tokenList: {
    data: { [merchantId: string]: GetTokenListData[] };
    loading: { [merchantId: string]: boolean };
    lastFetched: { [merchantId: string]: number };
    lastFailed?: { [merchantId: string]: number };
  };
}

// Retrieve and parse boolean values and user data from secureLocalStorage
const storedIsDark = secureLocalStorage.getItem("isDark");
const isDarkState = storedIsDark === "true";

const storedHasMerchant = secureLocalStorage.getItem("hasMerchant");
const hasMerchantState = storedHasMerchant === "true";

const storedUser = secureLocalStorage.getItem("user");
const parsedUser = storedUser ? JSON.parse(storedUser as string) : null;

const storedCurrentMerchantId = secureLocalStorage.getItem("currentMerchantId");
const currentMerchantId = storedCurrentMerchantId
  ? parseInt(storedCurrentMerchantId as string, 10)
  : -1;

const initialState: AppState = {
  isPersonal: currentMerchantId === -1,
  isDark: isDarkState !== null ? isDarkState : false,
  user: parsedUser || {
    userId: null,
    emailId: "",
    whatsappNumber: "",
    countryCode: "",
    userUUID: "",
    profileImage: "",
    firstName: "",
    currentMerchantId: currentMerchantId,
    merchantDetails: [],
    personalBalance: 0,
    notificationSound:
      parsedUser?.notificationSound !== undefined
        ? parsedUser.notificationSound
        : 0,
    merchantBalances: [],
    refBy: "",
    status: "offline",
    affiliateStatus: "inactive",
    accountType: "personal",
    institutionalMember: null,
    p2pAccess: {
      makerFiat: 0,
      takerFiat: 0,
      feeProfile: 0,
      makerCrypto: 0,
      takerCrypto: 0,
      makerP2pRampFiat: 0,
      takerP2pRampFiat: 0,
      makerP2pRampCrypto: 0,
      takerP2pRampCrypto: 0,
      isSendAllowed: 0,
    },
    kycStatuses: {
      idStatus: 0,
      addressStatus: 0,
    },
    createdAt: "",
    intercomUserHash: "",
  },
  profileMerchantData: null,
  websocketLoaded: false,
  hasMerchant: hasMerchantState !== null ? hasMerchantState : false,
  error: false,
  history: ["/"],
  addMerchant: false,
  // ✅ Initialize PrimeTaker State
  selectedTradeId: null,
  rampDetailsBuy: null,
  rampDetailsSell: null,
  reloadTrades: 0,
  isCreateMerchant: false,

  // Initialize token list state
  tokenList: {
    data: {},
    loading: {},
    lastFetched: {},
    lastFailed: {},
  },
};

type AppAction =
  | { type: typeof SET_SELECTED_TRADE_ID; payload: number | null }
  | { type: typeof SET_RAMP_DETAILS_BUY; payload: GetTradesForTakerData | null }
  | {
      type: typeof SET_RAMP_DETAILS_SELL;
      payload: GetTradesForTakerData | null;
    }
  | { type: typeof SET_RELOAD_TRADES; payload: number }
  | { type: typeof SET_IS_PERSONAL; payload: boolean }
  | { type: typeof SET_IS_DARK; payload: boolean }
  | { type: typeof SET_USER; payload: Partial<AppState["user"]> }
  | { type: typeof SET_PROFILE_LOADED; payload: boolean }
  | { type: typeof SET_HAS_MERCHANT; payload: boolean }
  | { type: typeof SET_WEBSOCKET_LOADED; payload: boolean }
  | { type: typeof SET_ERROR; payload: boolean }
  | { type: typeof ADD_HISTORY; payload: string }
  | { type: typeof RESET_STATE }
  | { type: typeof ADD_MERCHANT; payload: boolean }
  | { type: typeof SET_PROFILE_MERCHANT_DATA; payload: ProfileMerchantData }
  | {
      type: typeof SET_TOKEN_LIST;
      payload: { merchantId: string; data: GetTokenListData[] };
    }
  | {
      type: typeof SET_TOKEN_LIST_LOADING;
      payload: { merchantId: string; loading: boolean };
    }
  | { type: typeof CLEAR_TOKEN_LIST }
  | {
      type: typeof UPDATE_TOKEN_BALANCE;
      payload: {
        merchantId: string;
        currencySymbol: string;
        newBalance: number;
      };
    };

const appReducer = (state = initialState, action: AppAction): AppState => {
  switch (action.type) {
    case SET_SELECTED_TRADE_ID:
      return { ...state, selectedTradeId: action.payload };

    case SET_RAMP_DETAILS_BUY:
      return { ...state, rampDetailsBuy: action.payload };

    case SET_RAMP_DETAILS_SELL:
      return { ...state, rampDetailsSell: action.payload };

    case SET_RELOAD_TRADES:
      return { ...state, reloadTrades: action.payload };

    case SET_PROFILE_MERCHANT_DATA:
      return {
        ...state,
        profileMerchantData: action.payload,
      };

    case SET_IS_PERSONAL:
      return {
        ...state,
        isPersonal: action.payload,
      };
    case SET_IS_DARK:
      secureLocalStorage.setItem("isDark", action.payload.toString());
      return {
        ...state,
        isDark: action.payload,
      };
    case SET_USER:
      const updatedUser = {
        ...state.user,
        ...action.payload,
        p2pAccess: action.payload.p2pAccess || state.user.p2pAccess,
        kycStatuses: action.payload.kycStatuses || state.user.kycStatuses,
        status: action.payload.status || state.user.status,
        personalBalance:
          action.payload.personalBalance !== undefined
            ? action.payload.personalBalance
            : state.user.personalBalance,
        merchantBalances:
          action.payload.merchantBalances !== undefined
            ? action.payload.merchantBalances
            : state.user.merchantBalances,
        currentMerchantId:
          action.payload.currentMerchantId !== undefined
            ? action.payload.currentMerchantId
            : state.user.currentMerchantId,
        notificationSound:
          action.payload.notificationSound !== undefined
            ? action.payload.notificationSound
            : state.user.notificationSound,
      };

      // Store the updated user object and currentMerchantId in secureLocalStorage
      secureLocalStorage.setItem("user", JSON.stringify(updatedUser));
      secureLocalStorage.setItem(
        "currentMerchantId",
        updatedUser.currentMerchantId?.toString() || "-1",
      );

      return {
        ...state,
        user: updatedUser,
      };
    case SET_WEBSOCKET_LOADED:
      return {
        ...state,
        websocketLoaded: action.payload,
      };
    case SET_HAS_MERCHANT:
      secureLocalStorage.setItem("hasMerchant", action.payload.toString());
      return {
        ...state,
        hasMerchant: action.payload,
      };
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case ADD_HISTORY:
      return {
        ...state,
        history: [...state.history, action.payload],
      };
    case SET_TOKEN_LIST:
      return {
        ...state,
        tokenList: {
          ...state.tokenList,
          data: {
            ...state.tokenList.data,
            [action.payload.merchantId]: action.payload.data,
          },
          loading: {
            ...state.tokenList.loading,
            [action.payload.merchantId]: false,
          },
          lastFetched: {
            ...state.tokenList.lastFetched,
            [action.payload.merchantId]: Date.now(),
          },
        },
      };
    case SET_TOKEN_LIST_LOADING:
      return {
        ...state,
        tokenList: {
          ...state.tokenList,
          loading: {
            ...state.tokenList.loading,
            [action.payload.merchantId]: action.payload.loading,
          },
        },
      };
    case CLEAR_TOKEN_LIST:
      return {
        ...state,
        tokenList: {
          data: {},
          loading: {},
          lastFetched: {},
        },
      };
    case UPDATE_TOKEN_BALANCE:
      const { merchantId, currencySymbol, newBalance } = action.payload;
      const merchantTokenList = state.tokenList.data[merchantId];

      // If token list doesn't exist yet for this merchant, do nothing
      if (!merchantTokenList) {
        return state;
      }

      // Create a new token list with updated balance for the matching currency
      const updatedTokenList = merchantTokenList.map((token) => {
        if (token.currencySymbol === currencySymbol) {
          return { ...token, balance: newBalance };
        }
        return token;
      });

      return {
        ...state,
        tokenList: {
          ...state.tokenList,
          data: {
            ...state.tokenList.data,
            [merchantId]: updatedTokenList,
          },
        },
      };
    case RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

export default appReducer;
